import mapboxgl, {
  Map as MapboxMap,
  MapMouseEvent,
  MapTouchEvent,
  GeoJSONSource,
  ImageSource,
} from 'mapbox-gl';
import { Feature, Geometry, GeoJsonProperties } from 'geojson';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import {
  findThirdPoint,
  calculateAngleBetweenPoints,
  calculateCoordinates,
  Point,
  Transform,
} from '../geometry';
import {
  IMapControllerProps,
  MapEventType,
  MapEventHandler,
  IPoint,
  Coordinates,
  IMapData,
  IGeocoderResult,
} from '../types';
import { IPlan } from '../../../types/plans';
import { base64ToFile } from '../../../utils/utils';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

const satellite = 'mapbox://styles/mapbox/satellite-v9';

/**
 * Base Map Controller - handles core map functionality
 * - Map initialization and configuration
 * - Geocoder integration
 * - Address handling
 * - Plan rendering
 * - Screenshot functionality
 * - Mobile support
 */
export class BaseMapController {
  protected map: MapboxMap;
  protected center: IPoint;
  protected zoom: number;
  protected dragPan: boolean;
  public transform: Transform;
  protected plan: Partial<IPlan>;
  protected _handlers: Array<(data: IMapData) => void>;
  public onLoad: (value: boolean) => void;
  protected geocoder?: MapboxGeocoder;
  protected address?: string;
  protected state?: string;
  protected county?: string;
  protected city?: string;
  protected _loading: boolean;
  public isMobile: boolean;

  constructor({ ref, plan = {}, isMobile = false, center, zoom = 18 }: IMapControllerProps) {
    this.center = new Point(center ? center[0] : -122.108915, center ? center[1] : 37.370093);
    this.zoom = zoom;
    this.dragPan = true;
    this.transform = new Transform();
    this._loading = false;
    this.isMobile = isMobile;

    this.map = new mapboxgl.Map({
      container: ref.current as HTMLDivElement,
      style: satellite,
      center: this.center.toLngLat(),
      zoom: this.zoom,
      dragPan: this.dragPan,
    });

    this.plan = plan;
    this._handlers = [];
    this.onLoad = () => null;

    this.bindMethods();
  }

  private bindMethods() {
    this.enableGeocoder = this.enableGeocoder.bind(this);
    this.addGeocoderListener = this.addGeocoderListener.bind(this);
    this.removeGeocoderListener = this.removeGeocoderListener.bind(this);
    this.drawPlan = this.drawPlan.bind(this);
    this.setPlan = this.setPlan.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onMoveRotation = this.onMoveRotation.bind(this);
    this.onUpRotation = this.onUpRotation.bind(this);
    this.onUp = this.onUp.bind(this);
    this.update = this.update.bind(this);
    this.createRotateControl = this.createRotateControl.bind(this);
    this.onChange = this.onChange.bind(this);
    this.setHandler = this.setHandler.bind(this);
    this.takeScreenshot = this.takeScreenshot.bind(this);
  }

  // Core map methods
  getMap(): MapboxMap {
    return this.map;
  }

  get isReady(): boolean {
    return this.map.isStyleLoaded();
  }

  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this.onLoad(value);
    this._loading = value;
  }

  get data(): IMapData {
    return {
      center: this.center,
      rotation: this.transform.rotation,
      address: this.address || '',
      state: this.state || '',
      county: this.county || '',
      city: this.city || '',
    };
  }

  // Event handling
  onChange() {
    for (const handler of this._handlers) {
      handler(this.data);
    }
  }

  setHandler(handler: (data: IMapData) => void) {
    this._handlers.push(handler);
  }

  flyTo(center: [number, number]) {
    this.map.flyTo({
      center: center,
      zoom: 19,
      essential: false,
    });
  }

  setRotation(rotation: number) {
    this.transform.rotation = rotation;
  }

  // setViewState(state: { center?: [number, number]; rotation?: number }) {
  //   if (state.center) {
  //     this.center = new Point(state.center[0], state.center[1]);
  //     this.map.jumpTo({ center: this.center.toLngLat(), zoom: this.zoom });
  //   }

  //   if (state.rotation !== undefined) {
  //     this.transform.rotation = state.rotation;
  //   }

  //   if (this.plan?.planId) {
  //     this.clear();
  //     this.drawPlan();
  //   }

  //   this.onChange();
  // }

  takeScreenshot(): Promise<File> {
    return new Promise(resolve => {
      this.map.flyTo({
        center: this.center.toLngLat(),
        zoom: 19,
        essential: false,
      });
      this.map.once('idle', () => {
        resolve(
          base64ToFile(
            this.map.getCanvas().toDataURL('image/jpeg', 0.95),
            `map-snapshot-${Date.now()}`
          )
        );
      });
      this.map.once('moveend', () => {
        this.map.triggerRepaint();
      });
    });
  }

  enableGeocoder() {
    if (this.geocoder) {
      console.warn('Geocoder already exists, skipping creation');
      return;
    }

    this.geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken || '',
      // @ts-expect-error: Type mismatch between mapboxgl instance and expected typeof mapboxgl
      mapboxgl: mapboxgl,
      language: 'en',
      zoom: 19,
      placeholder: 'YOUR ADDRESS',
      countries: 'us',
      marker: true,
    });

    if (this.isMobile) {
      let geocoderContainer = document.getElementById('mobile-geocoder');

      if (!geocoderContainer) {
        geocoderContainer = document.querySelector('[id*="geocoder"]') as HTMLElement;
      }

      if (geocoderContainer) {
        geocoderContainer.innerHTML = '';
        const geocoderElement = this.geocoder.onAdd(this.map);
        geocoderContainer.appendChild(geocoderElement);
      } else {
        this.map.addControl(this.geocoder, 'top-left');
      }
    } else {
      this.map.addControl(this.geocoder, 'top-left');
    }

    this.setupGeocoderListeners();
  }

  private setupGeocoderListeners() {
    if (!this.geocoder) return;

    this.geocoder.on('result', ({ result }: IGeocoderResult) => {
      try {
        const { center } = result;
        this.center = new Point(center[0], center[1]);
        this.address = result.place_name;

        const { context, text } = result;
        let state: string | undefined;
        let county: string | undefined;
        let city: string | undefined;

        if (result.place_type && result.place_type.includes('place')) {
          city = text;
        }

        if (context && Array.isArray(context)) {
          context.forEach((c: { id: string; text: string }) => {
            if (c.id.startsWith('region')) {
              state = c.text;
            } else if (c.id.startsWith('district')) {
              county = c.text;
            } else if (c.id.startsWith('place') && !city) {
              city = c.text;
            }
          });
        }

        this.city = city;
        this.state = state;
        this.county = county;

        this.map.flyTo({
          center: center,
          zoom: 19,
          essential: false,
        });

        if (this.plan) this.drawPlan();
        this.onChange();
      } catch (error) {
        console.error('Error handling geocoder result:', error);
      }
    });
  }

  configureGeocoder(enabled: boolean): void {
    if (enabled) {
      this.enableGeocoder();
    } else {
      this.disableGeocoder();
    }
  }

  private disableGeocoder(): void {
    if (this.geocoder) {
      if (this.isMobile) {
        let geocoderContainer = document.getElementById('mobile-geocoder');
        if (!geocoderContainer) {
          geocoderContainer = document.querySelector('[id*="geocoder"]') as HTMLElement;
        }

        if (geocoderContainer) {
          geocoderContainer.innerHTML = '';
        }

        try {
          this.map.removeControl(this.geocoder);
        } catch {
          // Ignore error if control wasn't added to map
        }
      } else {
        try {
          this.map.removeControl(this.geocoder);
        } catch (error) {
          console.warn('⚠️ Failed to remove geocoder from map:', error);
        }
      }
      this.geocoder = undefined;
    }
  }

  addGeocoderListener(eventType: MapEventType, handler: MapEventHandler): void {
    this.geocoder?.on(eventType, handler);
  }

  removeGeocoderListener(eventType: MapEventType, handler: MapEventHandler): void {
    this.geocoder?.off(eventType, handler);
  }

  setGeocoderAddress(address: string): void {
    if (!this.geocoder) return;

    this.geocoder.query(address);

    this.address = address;
    this.onChange();
  }

  clearGeocoderAddress(): void {
    if (!this.geocoder) return;

    this.geocoder.clear();
    this.address = undefined;
    this.city = undefined;
    this.state = undefined;
    this.county = undefined;
    this.onChange();
  }

  setPlan(plan: Partial<IPlan>) {
    this.plan = plan;
    this.drawPlan();
  }

  drawPlan() {
    this.clear();

    if (!this.plan?.planId) return;
    const planId = this.plan.planId;
    const planMeta = {
      length: this.plan.meta?.length || 0,
      width: this.plan.meta?.width || 0,
    };

    this.loading = true;

    fetch(`https://3d.homewip.com/svg/${planId}/`)
      .then(response => response.text())
      .then(svgData => {
        const img = new Image();
        const svg = new Blob([svgData], {
          type: 'image/svg+xml;charset=utf-8',
        });
        const url = URL.createObjectURL(svg);

        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const imageData = canvas.toDataURL('image/png');
            const { coordinates } = calculateCoordinates(
              this.center.x,
              this.center.y,
              planMeta.length + 8,
              planMeta.width + 8,
              this.transform.rotation
            );

            this.clear();
            this.createPlanLayer(coordinates, imageData);
            this.createRotateControl(coordinates[1]);
            this.onChange();
          }

          URL.revokeObjectURL(url);
        };

        img.src = url;
      })
      .catch(error => console.error('Error loading SVG:', error))
      .finally(() => {
        this.loading = false;
      });
  }

  clear() {
    if (this.map.getLayer('svg-layer')) this.map.removeLayer('svg-layer');
    if (this.map.getLayer('plan-bounding')) this.map.removeLayer('plan-bounding');
    if (this.map.getLayer('plan')) this.map.removeLayer('plan');
    if (this.map.getLayer('rotate-layer')) this.map.removeLayer('rotate-layer');
    if (this.map.getLayer('rotate-control')) this.map.removeLayer('rotate-control');

    if (this.map.getSource('plan')) this.map.removeSource('plan');
    if (this.map.getSource('svg-source')) this.map.removeSource('svg-source');
    if (this.map.getSource('rotate-source')) this.map.removeSource('rotate-source');
    if (this.map.getSource('rotate-control')) this.map.removeSource('rotate-control');
  }

  clearRotateLayer() {
    if (this.map.getLayer('rotate-layer')) this.map.removeLayer('rotate-layer');
    if (this.map.getLayer('rotate-control')) this.map.removeLayer('rotate-control');
    if (this.map.getSource('rotate-source')) this.map.removeSource('rotate-source');
    if (this.map.getSource('rotate-control')) this.map.removeSource('rotate-control');
  }

  createRotateLayer() {
    const { coordinates } = calculateCoordinates(
      this.center.x,
      this.center.y,
      (this.plan.meta?.length || 0) + 8,
      (this.plan.meta?.width || 0) + 8,
      this.transform.rotation
    );
    this.createRotateControl(coordinates[1]);
  }

  onMove(e: MapMouseEvent | MapTouchEvent) {
    const coords = e.lngLat;
    this.map.getCanvas().style.cursor = 'grabbing';

    if (this.transform.offset.isZero())
      this.transform.offset = new Point(this.center.x - coords.lng, this.center.y - coords.lat);

    this.center = new Point(
      coords.lng + this.transform.offset.x,
      coords.lat + this.transform.offset.y
    );
    this.update();
  }

  onMoveRotation(e: MapMouseEvent | MapTouchEvent) {
    const coords = e.lngLat;
    const defaultCoords = calculateCoordinates(
      this.center.x,
      this.center.y,
      (this.plan.meta?.length || 0) + 8,
      (this.plan.meta?.width || 0) + 8,
      0
    );
    const zeroCoords: IPoint = new Point(
      this.center.x + defaultCoords.lngDiff,
      this.center.y + defaultCoords.latDiff
    );
    const angle = calculateAngleBetweenPoints(
      zeroCoords,
      this.center,
      new Point(coords.lng, coords.lat)
    );

    this.transform.rotation = angle * -1;
    this.update();
  }

  onUpRotation() {
    this.map.getCanvas().style.cursor = '';
    this.map.off('mousemove', this.onMoveRotation);
    this.map.off('touchmove', this.onMoveRotation);
    this.onChange();
  }

  onUp() {
    this.map.getCanvas().style.cursor = '';
    this.transform.offset.x = 0;
    this.transform.offset.y = 0;
    this.map.off('mousemove', this.onMove);
    this.map.off('touchmove', this.onMove);
    this.onChange();
  }

  update() {
    const { coordinates } = calculateCoordinates(
      this.center.x,
      this.center.y,
      (this.plan.meta?.length || 0) + 8,
      (this.plan.meta?.width || 0) + 8,
      this.transform.rotation
    );

    const imageCoordinates: Coordinates = [
      coordinates[0].toLngLat(),
      coordinates[1].toLngLat(),
      coordinates[2].toLngLat(),
      coordinates[3].toLngLat(),
    ];

    const centerRotation = findThirdPoint(this.center, coordinates[1], 0.00001);
    const coordinatesRotate = calculateCoordinates(
      centerRotation.x,
      centerRotation.y,
      6,
      6,
      this.transform.rotation
    );

    const rotateCoordinates: Coordinates = [
      coordinatesRotate.coordinates[0].toLngLat(),
      coordinatesRotate.coordinates[1].toLngLat(),
      coordinatesRotate.coordinates[2].toLngLat(),
      coordinatesRotate.coordinates[3].toLngLat(),
    ];

    const planData: Feature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[...coordinates.map(coord => coord.toLngLat()), coordinates[0].toLngLat()]],
      },
    };

    const controlData: Feature = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            ...coordinatesRotate.coordinates.map(coord => coord.toLngLat()),
            coordinatesRotate.coordinates[0].toLngLat(),
          ],
        ],
      },
    };

    const planSource = this.map.getSource('plan') as GeoJSONSource;
    const svgSource = this.map.getSource('svg-source') as ImageSource;
    const rotateSource = this.map.getSource('rotate-source') as ImageSource;
    const rotateControlSource = this.map.getSource('rotate-control') as GeoJSONSource;

    if (planSource?.setData) planSource.setData(planData);
    if (svgSource?.setCoordinates) svgSource.setCoordinates(imageCoordinates);
    if (rotateSource?.setCoordinates) rotateSource.setCoordinates(rotateCoordinates);
    if (rotateControlSource?.setData) rotateControlSource.setData(controlData);
  }

  private createPlanLayer(coordinates: IPoint[], imageData: string) {
    const imageCoordinates: Coordinates = [
      coordinates[0].toLngLat(),
      coordinates[1].toLngLat(),
      coordinates[2].toLngLat(),
      coordinates[3].toLngLat(),
    ];

    this.map.addSource('svg-source', {
      type: 'image',
      url: imageData,
      coordinates: imageCoordinates,
    });

    const planData: Feature<Geometry, GeoJsonProperties> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [[...coordinates.map(coord => coord.toLngLat()), coordinates[0].toLngLat()]],
      },
    };

    this.map.addSource('plan', {
      type: 'geojson',
      data: planData,
    });

    this.map.addLayer({
      id: 'plan',
      type: 'fill',
      source: 'plan',
      layout: {},
      paint: {
        'fill-color': '#4bc484',
        'fill-opacity': 0.3,
      },
    });

    this.map.addLayer({
      id: 'plan-bounding',
      type: 'line',
      source: 'plan',
      layout: {},
      paint: {
        'line-color': '#000',
        'line-width': 1,
      },
    });

    this.map.addLayer({
      id: 'svg-layer',
      type: 'raster',
      source: 'svg-source',
    });

    this.setupPlanInteractions();
  }

  private setupPlanInteractions() {
    this.map.on('mousedown', 'plan', e => {
      e.preventDefault();
      this.map.getCanvas().style.cursor = 'grabbing';
      this.map.on('mousemove', this.onMove);
      this.map.once('mouseup', this.onUp);
    });

    this.map.on('touchstart', 'plan', e => {
      if (e.points.length !== 1) return;
      e.preventDefault();
      this.map.on('touchmove', this.onMove);
      this.map.once('touchend', this.onUp);
    });

    this.map.on('mouseup', 'plan', () => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.on('mouseenter', 'plan', () => {
      this.map.getCanvas().style.cursor = 'move';
    });

    this.map.on('mouseleave', 'plan', () => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  private createRotateControl(coordinates: IPoint) {
    const centerRotation = findThirdPoint(this.center, coordinates, 0.00001);
    const coordinatesRotate = calculateCoordinates(
      centerRotation.x,
      centerRotation.y,
      6,
      6,
      this.transform.rotation
    );

    const rotateCoordinates: Coordinates = [
      coordinatesRotate.coordinates[0].toLngLat(),
      coordinatesRotate.coordinates[1].toLngLat(),
      coordinatesRotate.coordinates[2].toLngLat(),
      coordinatesRotate.coordinates[3].toLngLat(),
    ];

    this.map.addSource('rotate-source', {
      type: 'image',
      url: '/public/icons/rotation_icon.png',
      coordinates: rotateCoordinates,
    });

    const controlData: Feature<Geometry, GeoJsonProperties> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            ...coordinatesRotate.coordinates.map(coord => coord.toLngLat()),
            coordinatesRotate.coordinates[0].toLngLat(),
          ],
        ],
      },
    };

    this.map.addSource('rotate-control', {
      type: 'geojson',
      data: controlData,
    });

    this.map.addLayer({
      id: 'rotate-control',
      type: 'fill',
      source: 'rotate-control',
      layout: {},
      paint: {
        'fill-color': '#4bc484',
        'fill-opacity': 0.0,
      },
    });

    this.map.addLayer({
      id: 'rotate-layer',
      type: 'raster',
      source: 'rotate-source',
    });

    this.setupRotateInteractions();
  }

  private setupRotateInteractions() {
    this.map.on('mousedown', 'rotate-control', e => {
      e.preventDefault();
      this.map.on('mousemove', this.onMoveRotation);
      this.map.once('mouseup', this.onUpRotation);
    });

    this.map.on('touchstart', 'rotate-control', e => {
      if (e.points.length !== 1) return;
      e.preventDefault();
      this.map.on('touchmove', this.onMoveRotation);
      this.map.once('touchend', this.onUpRotation);
    });
  }
}
